import { useEffect } from 'react';
import { useStore } from '@/store/useStore';
import { TransactionType, TransactionStatus } from '@/types';
import { ArrowUpRight, ArrowDownLeft, Trophy, Gift, Clock, CheckCircle, XCircle } from 'lucide-react';

export function TransactionsPage() {
  const { transactions, fetchTransactions } = useStore();

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const getTransactionIcon = (type: TransactionType) => {
    switch (type) {
      case TransactionType.DEPOSIT:
        return <ArrowDownLeft className="w-5 h-5 text-green-500" />;
      case TransactionType.RAFFLE_ENTRY:
        return <ArrowUpRight className="w-5 h-5 text-red-500" />;
      case TransactionType.RAFFLE_WIN:
        return <Trophy className="w-5 h-5 text-yellow-500" />;
      case TransactionType.REFERRAL_BONUS:
        return <Gift className="w-5 h-5 text-purple-500" />;
      case TransactionType.JACKPOT_BET:
        return <ArrowUpRight className="w-5 h-5 text-orange-500" />;
      case TransactionType.JACKPOT_WIN:
        return <Trophy className="w-5 h-5 text-yellow-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getTransactionTypeLabel = (type: TransactionType) => {
    switch (type) {
      case TransactionType.DEPOSIT:
        return 'Пополнение';
      case TransactionType.RAFFLE_ENTRY:
        return 'Вход в розыгрыш';
      case TransactionType.RAFFLE_WIN:
        return 'Выигрыш в розыгрыше';
      case TransactionType.REFERRAL_BONUS:
        return 'Реферальный бонус';
      case TransactionType.JACKPOT_BET:
        return 'Ставка в джекпоте';
      case TransactionType.JACKPOT_WIN:
        return 'Выигрыш в джекпоте';
      case TransactionType.REFUND:
        return 'Возврат средств';
      default:
        return 'Неизвестно';
    }
  };

  const getStatusIcon = (status: TransactionStatus) => {
    switch (status) {
      case TransactionStatus.COMPLETED:
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case TransactionStatus.PENDING:
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case TransactionStatus.FAILED:
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status: TransactionStatus) => {
    switch (status) {
      case TransactionStatus.COMPLETED:
        return 'Завершено';
      case TransactionStatus.PENDING:
        return 'В обработке';
      case TransactionStatus.FAILED:
        return 'Ошибка';
      default:
        return 'Неизвестно';
    }
  };

  const getAmountColor = (type: TransactionType) => {
    switch (type) {
      case TransactionType.DEPOSIT:
      case TransactionType.RAFFLE_WIN:
      case TransactionType.REFERRAL_BONUS:
      case TransactionType.JACKPOT_WIN:
      case TransactionType.REFUND:
        return 'text-green-600 dark:text-green-400';
      case TransactionType.RAFFLE_ENTRY:
      case TransactionType.JACKPOT_BET:
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Только что';
    if (diffMins < 60) return `${diffMins} мин назад`;
    if (diffHours < 24) return `${diffHours} ч назад`;
    if (diffDays < 7) return `${diffDays} дн назад`;

    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  return (
    <div className="min-h-screen bg-tg-theme-bg pb-20">
      {/* Header */}
      <div className="bg-gradient-to-br from-purple-600 to-blue-600 text-white p-6">
        <h1 className="text-2xl font-bold mb-2">История транзакций</h1>
        <p className="text-purple-100 text-sm">
          Все ваши операции с балансом
        </p>
      </div>

      {/* Transactions List */}
      <div className="p-4 space-y-3">
        {transactions.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="w-16 h-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400 mb-2">
              Пока нет транзакций
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-600">
              Здесь будет отображаться история ваших операций
            </p>
          </div>
        ) : (
          transactions.map((transaction) => (
            <div
              key={transaction.id}
              className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  {/* Icon */}
                  <div className="mt-1">
                    {getTransactionIcon(transaction.type)}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="font-semibold text-tg-theme-text">
                        {getTransactionTypeLabel(transaction.type)}
                      </h3>
                      <div className="flex items-center space-x-1">
                        {getStatusIcon(transaction.status)}
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {getStatusLabel(transaction.status)}
                        </span>
                      </div>
                    </div>

                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                      {formatDate(transaction.created_at)}
                    </p>

                    {/* Metadata */}
                    {transaction.metadata && (
                      <div className="text-xs text-gray-400 dark:text-gray-600">
                        {transaction.metadata.raffle_title && (
                          <p>Розыгрыш: {transaction.metadata.raffle_title}</p>
                        )}
                        {transaction.metadata.tickets_count && (
                          <p>Билетов: {transaction.metadata.tickets_count}</p>
                        )}
                        {transaction.metadata.referrer_username && (
                          <p>От: @{transaction.metadata.referrer_username}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Amount */}
                <div className="text-right ml-4">
                  <p className={`font-bold text-lg ${getAmountColor(transaction.type)}`}>
                    {[TransactionType.RAFFLE_ENTRY, TransactionType.JACKPOT_BET].includes(transaction.type) ? '-' : '+'}
                    {transaction.amount.toFixed(2)} ₽
                  </p>
                  {transaction.payment_method && (
                    <p className="text-xs text-gray-400 dark:text-gray-600 mt-1">
                      {transaction.payment_method === 'test' ? 'Тест' : transaction.payment_method}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Summary */}
      {transactions.length > 0 && (
        <div className="p-4 mt-4">
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-xl p-4 border border-purple-100 dark:border-purple-800">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Сводка
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Всего операций
                </p>
                <p className="text-xl font-bold text-tg-theme-text">
                  {transactions.length}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Успешных
                </p>
                <p className="text-xl font-bold text-green-600 dark:text-green-400">
                  {transactions.filter(t => t.status === TransactionStatus.COMPLETED).length}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
